import React from 'react';
import { useHistory } from 'react-router-dom';
import { TabPanel } from 'primereact/tabview';
import { TabViewStyled } from 'styles/v2/Styles.styled';
import { EpdLinks } from 'routes/EpdRoutes';
import AdditionalInformationTab from './AdditionalInformationTab';
import { CompilerFieldMatcher, getStateForFields } from './CompilerStepsValidator';
import StepTabHeader from './StepTabHeader';
import DefinitionTab from './DefinitionTab';
import OrganizationTab from './OrganizationTab';
import PcrLcaAndVerificationTab from './PcrLcaAndVerificationTab';
import { compilerStep1Tabs } from './compilerNavConstants';
import { CompilerCompositeStatusModel } from 'types/types';

const epdDefinitionTabFields: CompilerFieldMatcher[] = [
  'generalInformation',
  'title',
  'statementOfConformityWithStandards',
  'versionDescription',
  'requirePublicationOnOkobaudat',
  'coverPageProductImage',
  'useAdvice',
  'subtype',
  'epdClassification',
  'representativeJustification',
  'determinationOfAverageDescription',
  'listOfContributingManufacturers',
  'sitesSelectionDescription',
  'documentCoverageStatement',
  'productToDeclare',
  'functionalUnitDescription',
  'productFlow',
  'productFlowName',
  'productFlowUnit',
  'productFlowValue',
  'conversionFactor',
  'conversionFactorUnit',
  'conversionFactorToMass',
  'massOfDeclaredUnit',
  'customLogotype',
  'references',
];

const pcrLcaEpdVerificationTabFields: CompilerFieldMatcher[] = [
  'pcr',
  'cpcrProductCategory',
  'lcaPractitioners',
  'verificationType',
  'thirdPartyVerification',
  'processCertificateReports',
  'preverifiedTool',
  'preverifiedToolVersion',
  'epdVerifier',
  'epdVerifierUnavailable',
];

const organisationInformationTabFields: CompilerFieldMatcher[] = [
  'companyLogoImages',
  'organizationInformation',
  'organizationInformationAddress',
];

const additionalInformationTabFields: CompilerFieldMatcher[] = [
  'epdAdditionalInformation',
  'abbreviations',
  'environmental',
  'economic',
];

const Step1: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  tab: string | null | undefined;
  validationState: CompilerCompositeStatusModel | undefined;
}> = ({ epdId, epdVersionId, tab, validationState }) => {
  const history = useHistory();

  let tabIndex = compilerStep1Tabs.findIndex((t) => t === tab);
  if (tabIndex === -1) {
    tabIndex = 0;
  }

  const errors = validationState?.errors;

  const epdDefinitionTabState = getStateForFields(epdDefinitionTabFields, errors);
  const pcrLcaEpdVerificationTabState = getStateForFields(pcrLcaEpdVerificationTabFields, errors);
  const organisationInformationTabState = getStateForFields(organisationInformationTabFields, errors);
  const additionalInformationTabState = getStateForFields(additionalInformationTabFields, errors);

  return (
    <TabViewStyled
      activeIndex={tabIndex}
      onTabChange={(e) =>
        history.push(EpdLinks.epdCompilerSpecific(epdId, { step: 'step1', tab: compilerStep1Tabs[e.index] }))
      }
    >
      <TabPanel header={<StepTabHeader title="EPD definition" state={epdDefinitionTabState} />}>
        <DefinitionTab epdId={epdId} epdVersionId={epdVersionId!} validationState={validationState} />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="PCR, LCA, EPD verification" state={pcrLcaEpdVerificationTabState} />}>
        <PcrLcaAndVerificationTab epdId={epdId} epdVersionId={epdVersionId} validationState={validationState} />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="Organisation information" state={organisationInformationTabState} />}>
        <OrganizationTab
          epdId={epdId}
          epdVersionId={epdVersionId}
          canUpload={false}
          isReadOnly={false}
          validationState={validationState}
        />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="Additional information" state={additionalInformationTabState} />}>
        <AdditionalInformationTab
          epdId={epdId}
          epdVersionId={epdVersionId!}
          isReadOnly={false}
          validationState={validationState}
        />
      </TabPanel>
    </TabViewStyled>
  );
};

export default Step1;
